/*
 * SPDX-FileCopyrightText: 2022 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { DeleteApiRequestBuilder } from '../common/api-request-builder/delete-api-request-builder';
import { GetApiRequestBuilder } from '../common/api-request-builder/get-api-request-builder';
import { PostApiRequestBuilder } from '../common/api-request-builder/post-api-request-builder';
import type {
  ImageProxyRequestDto,
  ImageProxyResponse,
  MediaUpload
} from './types';

/**
 * Requests an image-proxy URL from the backend for a given image URL.
 *
 * @param imageUrl The image URL which should be proxied.
 * @return The proxy URL for the image.
 * @throws {Error} when the api request wasn't successful.
 */
export const getProxiedUrl = async (
  imageUrl: string
): Promise<ImageProxyResponse> => {
  const response = await new PostApiRequestBuilder<
    ImageProxyResponse,
    ImageProxyRequestDto
  >('media/proxy')
    .withJsonBody({
      url: imageUrl
    })
    .sendRequest();
  return response.asParsedJsonObject();
};

export const getImageLibrary = async (name_q = null, current_page = 1) => {
  let link = `cms/media/search/?per_page=20&current_page=${current_page}`;
  if (name_q) {
    link += `&name_q=${name_q}`;
  }
  const response = await new GetApiRequestBuilder<any>(
    '',
    '',
    link
  ).sendRequest();

  return response.asParsedJsonObject();
};
/**
 * Uploads a media file to the backend.
 *
 * @param noteIdOrAlias The id or alias of the note from which the media is uploaded.
 * @param media The binary media content.
 * @return The URL of the uploaded media object.
 * @throws {Error} when the api request wasn't successful.
 */
export const uploadFile = async (
  noteIdOrAlias: string,
  media: Blob
): Promise<any> => {
  const postData = new FormData();
  postData.append('image', media);
  postData.append('filename', media.name);
  try {
    const response = await new PostApiRequestBuilder<any, void>(
      '',
      '',
      'cms/media/'
    )
      .withBody(postData)
      .sendRequest();

    return {
      success: true,
      data: response.asParsedJsonObject()
    };
  } catch (error) {
    return {
      success: false,
      error
    };
  }
};

/**
 * Deletes some uploaded media object.
 *
 * @param mediaId The identifier of the media object to delete.
 * @throws {Error} when the api request wasn't successful.
 */
export const deleteUploadedMedia = async (mediaId: string): Promise<void> => {
  await new DeleteApiRequestBuilder('media/' + mediaId).sendRequest();
};
